import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { advertiserApi, type Campaign, type CampaignStatus } from '../../services/advertiserApi';
import { CampaignFormDialog } from './CampaignFormDialog';

function dollars(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function statusColor(status: CampaignStatus): 'default' | 'success' | 'warning' | 'info' {
  if (status === 'active') return 'success';
  if (status === 'paused') return 'warning';
  if (status === 'ended' || status === 'archived') return 'default';
  return 'info';
}

/**
 * Campaigns list — table of every campaign for the advertiser, with
 * status chip, budget, dates, spend, and Manage / Pause-Resume actions.
 *
 * The "?new=1" query parameter (set by the Create CTA on the dashboard
 * overview) auto-opens the create dialog so the user can come straight
 * from the overview without an extra click.
 */
export function CampaignsListPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);

  const campaignsQuery = useQuery({
    queryKey: ['advertiser', 'campaigns'],
    queryFn: () => advertiserApi.listCampaigns(),
  });

  const pauseMutation = useMutation({
    mutationFn: (id: string) => advertiserApi.pauseCampaign(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['advertiser', 'campaigns'] }),
  });
  const resumeMutation = useMutation({
    mutationFn: (id: string) => advertiserApi.resumeCampaign(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['advertiser', 'campaigns'] }),
  });

  // Deep-link support: `?new=1` opens the create dialog once, then strips the
  // param. This genuinely belongs in an effect — it performs navigation
  // (setSearchParams), which must not run during render — so the
  // set-state-in-effect heuristic is suppressed deliberately for this block.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setEditing(null);
      setFormOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  /* eslint-enable react-hooks/set-state-in-effect */

  function onNewCampaign() {
    setEditing(null);
    setFormOpen(true);
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem' }}>
          Campaigns
        </Typography>
        <Button variant="contained" onClick={onNewCampaign}>
          New campaign
        </Button>
      </Stack>

      {campaignsQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading campaigns" />
        </Box>
      ) : campaignsQuery.isError ? (
        <Alert severity="error" role="alert">
          Failed to load campaigns: {(campaignsQuery.error as Error).message}
        </Alert>
      ) : (campaignsQuery.data?.campaigns.length ?? 0) === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body1">You haven’t created any campaigns yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Create one to start sponsoring product listings.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table aria-label="Your campaigns">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Daily budget</TableCell>
                <TableCell>Total budget</TableCell>
                <TableCell>Spent</TableCell>
                <TableCell>Starts</TableCell>
                <TableCell>Ends</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaignsQuery.data!.campaigns.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>
                    <Chip label={c.status} size="small" color={statusColor(c.status)} />
                  </TableCell>
                  <TableCell>{dollars(c.dailyBudgetCents)}</TableCell>
                  <TableCell>{dollars(c.totalBudgetCents)}</TableCell>
                  <TableCell>{dollars(c.spentCents)}</TableCell>
                  <TableCell>{c.startsAt ? c.startsAt.slice(0, 10) : '—'}</TableCell>
                  <TableCell>{c.endsAt ? c.endsAt.slice(0, 10) : '—'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {c.status === 'paused' ? (
                        <Button
                          size="small"
                          onClick={() => resumeMutation.mutate(c.id)}
                          disabled={resumeMutation.isPending}
                        >
                          Resume
                        </Button>
                      ) : c.status === 'active' ? (
                        <Button
                          size="small"
                          onClick={() => pauseMutation.mutate(c.id)}
                          disabled={pauseMutation.isPending}
                        >
                          Pause
                        </Button>
                      ) : null}
                      <Button
                        size="small"
                        component={RouterLink}
                        to={`/dashboard/advertiser/campaigns/${c.id}`}
                      >
                        Manage
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CampaignFormDialog
        open={formOpen}
        campaign={editing}
        onClose={() => setFormOpen(false)}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['advertiser', 'campaigns'] })}
      />
    </Box>
  );
}

export default CampaignsListPage;
